import { WizardBackground } from "@/components/mentor-profile/wizard-background";
import { Wordmark } from "@/components/brand/wordmark";
import { cn } from "@/lib/utils";
import { Button } from "@somethings-ui/web";
import { ArrowLeft } from "lucide-react";
import { CSSProperties } from "react";

interface WizardIntroProps {
  onContinue: () => void;
  onBack?: () => void;
}

// Fanned deck of real example mentor profiles (screenshots in public/assets).
// Each phone springs out from a centered stack via the .device-fan animation:
// --fx/--fy/--fr/--fs place it in the fan, delay staggers it centre-first.
// Outer phones are hidden below lg so smaller screens show a tidy trio.
interface FanPhone {
  name: string;
  src: string;
  fx: string;
  fy: string;
  fr: string;
  fs: string;
  z: string;
  delay: number;
  hideOnSmall?: boolean;
}

const FAN_PHONES: FanPhone[] = [
  { name: "Ty", src: "/assets/mentor-onboarding/example-ty.png", fx: "-96px", fy: "28px", fr: "-8deg", fs: "0.93", z: "z-20", delay: 520 },
  { name: "Angela", src: "/assets/mentor-onboarding/example-khayla.png", fx: "0px", fy: "-10px", fr: "0deg", fs: "1", z: "z-40", delay: 400 },
  { name: "Darius", src: "/assets/mentor-onboarding/example-darius.png", fx: "-210px", fy: "62px", fr: "-16deg", fs: "0.84", z: "z-10", delay: 640, hideOnSmall: true },
  { name: "Mohammed", src: "/assets/mentor-onboarding/example-mohammed.png", fx: "96px", fy: "28px", fr: "8deg", fs: "0.93", z: "z-20", delay: 520 },
  { name: "Emma", src: "/assets/mentor-onboarding/example-emma.png", fx: "210px", fy: "62px", fr: "16deg", fs: "0.84", z: "z-10", delay: 640, hideOnSmall: true },
];

export function WizardIntro({ onContinue, onBack }: WizardIntroProps) {
  return (
    <WizardBackground className="flex-col justify-between gap-10 py-10">
      <header className="relative z-10 flex w-full max-w-[1200px] items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={!onBack}
          className="flex items-center gap-2 font-body text-[17px] text-[#797774] transition-colors hover:text-[#1f1e1d] disabled:invisible"
        >
          <ArrowLeft className="size-5" />
          Back
        </button>
        <Wordmark className="h-[22px] text-black" />
        <span className="w-16" />
      </header>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <h1 className="font-heading text-[54px] font-bold leading-[1.02] tracking-[-1.6px] text-[#1f1e1d]">
            Here&apos;s what you&apos;re building
          </h1>
          <p className="max-w-[705px] font-body text-[20px] leading-[1.6] text-[#797774]">
            A few examples of stand out mentor profiles. A great profile helps teens find the right mentor and get to know you before you start chatting. 
          </p>
        </div>

        <div className="relative h-[400px] w-full max-w-[760px] pt-2 sm:h-[480px]">
          {FAN_PHONES.map((phone) => (
            <div
              key={phone.name}
              className={cn(
                "device-fan absolute left-1/2 top-1/2 w-[42vw] max-w-[200px]",
                phone.z,
                phone.hideOnSmall && "hidden lg:block",
              )}
              style={
                {
                  "--fx": phone.fx,
                  "--fy": phone.fy,
                  "--fr": phone.fr,
                  "--fs": phone.fs,
                  animationDelay: `${phone.delay}ms`,
                } as CSSProperties
              }
            >
              <div className="overflow-hidden rounded-[30px] bg-neutral-900 p-[5px] shadow-[0px_0px_0px_1px_rgba(0,0,0,0.1),0px_26px_49px_-26px_rgba(31,30,29,0.6)]">
                <div className="relative aspect-[9/19] overflow-hidden rounded-[25px] bg-white">
                  <img
                    src={phone.src}
                    alt={`${phone.name}'s mentor profile`}
                    className="size-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 flex w-full max-w-[440px] flex-col items-center gap-3">
        <Button onPress={onContinue} fullWidth>
          Continue
        </Button>
        <p className="font-body text-[15px] text-[#797774]">
          About 10 minutes · stop and come back anytime
        </p>
      </div>
    </WizardBackground>
  );
}
